import type { AuthResponse, SignUpWithMerchantIdRequest } from '../../types';
import type {
  AdminSignInRequest,
  AuthorizeResponse,
  CreateInternalUserRequest,
  CreateTenantUserRequest,
  OnboardMerchantRequest,
  OnboardMerchantResponse,
  SignupToggleRequest,
  SignupToggleResponse,
} from '../types';
import type { RequestFn } from '../../client';

export class Admin {
  constructor(private readonly request: RequestFn) {}

  async signIn(params: AdminSignInRequest): Promise<AuthResponse> {
    return this.request('POST', '/admin/signin', { body: params });
  }

  async createInternalUser(params: CreateInternalUserRequest): Promise<AuthorizeResponse> {
    return this.request('POST', '/admin/internal-signup', { body: params });
  }

  async createTenant(params: CreateTenantUserRequest): Promise<AuthorizeResponse> {
    return this.request('POST', '/admin/tenant-signup', { body: params });
  }

  /**
   * Create a new merchant admin user and merchant account atomically.
   *
   * This is the correct endpoint for bootstrapping the first admin user in a
   * fresh deployment — `internal-signup`/`tenant-signup` both require
   * pre-existing merchant records that don't exist on a clean database.
   *
   * `POST /admin/signup-with-merchant-id`
   */
  async signupWithMerchantId(params: SignUpWithMerchantIdRequest): Promise<AuthorizeResponse> {
    return this.request('POST', '/admin/signup-with-merchant-id', { body: params });
  }

  /** Toggle public signup on/off. `POST /admin/settings/signup` */
  async setSignupSettings(params: SignupToggleRequest): Promise<SignupToggleResponse> {
    return this.request('POST', '/admin/settings/signup', { body: params });
  }

  /** Read current public-signup status. `GET /admin/settings/signup` */
  async getSignupSettings(): Promise<SignupToggleResponse> {
    return this.request('GET', '/admin/settings/signup');
  }

  /** Full merchant bootstrap — user + merchant + project + profile + keys. `POST /admin/onboard-merchant` */
  async onboardMerchant(params: OnboardMerchantRequest): Promise<OnboardMerchantResponse> {
    return this.request('POST', '/admin/onboard-merchant', { body: params });
  }
}
